import React, { useState, useEffect } from 'react';
import PromptCard from '../PromptCard';
import PromptDetailModal from '../PromptDetailModal';
import { PromptService } from '../../services/prompts';
import { Prompt, PromptListQuery } from '../../services/prompts';

interface TrashPageProps {
  onNavigate: (page: string) => void;
}

export function TrashPage({ onNavigate }: TrashPageProps): JSX.Element {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPrompts, setSelectedPrompts] = useState<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [showModal, setShowModal] = useState(false);

  // 加载回收站中的提示词
  const loadTrashPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      const query: PromptListQuery = {
        search: searchQuery,
        limit: 100,
        onlyDeleted: true
      };
      const response = await PromptService.getTrashPrompts(query);
      setPrompts(response.prompts);
    } catch (err) {
      console.error('加载回收站失败:', err);
      setError('加载回收站失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrashPrompts();
  }, [searchQuery]);

  // 恢复单个提示词
  const handleRestore = async (promptId: string) => {
    try {
      await PromptService.restorePrompt(promptId);
      await loadTrashPrompts();
      alert('提示词恢复成功！');
    } catch (err) {
      console.error('恢复失败:', err);
      alert('恢复失败，请稍后重试');
    }
  };

  // 永久删除单个提示词
  const handlePermanentDelete = async (promptId: string) => {
    if (!confirm('确定要永久删除这个提示词吗？此操作无法撤销！')) {
      return;
    }
    
    try {
      await PromptService.permanentDeletePrompt(promptId);
      await loadTrashPrompts();
      alert('提示词已永久删除');
    } catch (err) {
      console.error('永久删除失败:', err);
      alert('删除失败，请稍后重试');
    }
  };

  // 批量恢复
  const handleBatchRestore = async () => {
    if (selectedPrompts.size === 0) {
      alert('请先选择要恢复的提示词');
      return;
    }

    try {
      const promises = Array.from(selectedPrompts).map(id => 
        PromptService.restorePrompt(id)
      );
      await Promise.all(promises);
      await loadTrashPrompts();
      setSelectedPrompts(new Set());
      setBatchMode(false);
      alert(`成功恢复 ${promises.length} 个提示词！`);
    } catch (err) {
      console.error('批量恢复失败:', err);
      alert('批量恢复失败，请稍后重试');
    }
  };

  // 批量永久删除
  const handleBatchPermanentDelete = async () => {
    if (selectedPrompts.size === 0) {
      alert('请先选择要删除的提示词');
      return;
    }

    if (!confirm(`确定要永久删除选中的 ${selectedPrompts.size} 个提示词吗？此操作无法撤销！`)) {
      return;
    }

    try {
      const promises = Array.from(selectedPrompts).map(id => 
        PromptService.permanentDeletePrompt(id)
      );
      await Promise.all(promises);
      await loadTrashPrompts();
      setSelectedPrompts(new Set());
      setBatchMode(false);
      alert(`成功删除 ${promises.length} 个提示词！`);
    } catch (err) {
      console.error('批量删除失败:', err);
      alert('批量删除失败，请稍后重试');
    }
  };

  // 清空回收站
  const handleEmptyTrash = async () => {
    if (prompts.length === 0) {
      alert('回收站已经是空的');
      return;
    }

    if (!confirm(`确定要清空回收站吗？这将永久删除所有 ${prompts.length} 个提示词，此操作无法撤销！`)) {
      return;
    }

    try {
      const response = await PromptService.emptyTrash();
      await loadTrashPrompts();
      alert(`回收站已清空，共删除 ${response.deleted} 个提示词`);
    } catch (err) {
      console.error('清空回收站失败:', err);
      alert('清空回收站失败，请稍后重试');
    }
  };

  // 自动清理过期项目
  const handleAutoCleanup = async () => {
    try {
      const response = await PromptService.autoCleanupTrash();
      await loadTrashPrompts();
      if (response.deleted > 0) {
        alert(`自动清理完成，删除了 ${response.deleted} 个过期项目`);
      } else {
        alert('没有过期项目需要清理');
      }
    } catch (err) {
      console.error('自动清理失败:', err);
      alert('自动清理失败，请稍后重试');
    }
  };

  // 计算剩余天数
  const getRemainingDays = (deletedAt: number): number => {
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
    const expiryTime = deletedAt + sevenDaysInMs;
    const remainingMs = expiryTime - Date.now();
    return Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));
  };

  // 处理提示词选择
  const handlePromptSelect = (promptId: string, selected: boolean) => {
    const newSelected = new Set(selectedPrompts);
    if (selected) {
      newSelected.add(promptId);
    } else {
      newSelected.delete(promptId);
    }
    setSelectedPrompts(newSelected);
  };

  // 查看提示词详情
  const handleViewPrompt = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            🗑️ 回收站
          </h1>
          <p className="text-gray-600 mt-1">
            已删除的提示词将在7天后自动永久删除
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAutoCleanup}
            className="px-4 py-2 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
          >
            清理过期
          </button>
          <button
            onClick={handleEmptyTrash}
            className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            disabled={prompts.length === 0}
          >
            清空回收站
          </button>
        </div>
      </div>

      {/* 搜索和操作栏 */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <input
            type="text"
            placeholder="搜索回收站中的提示词..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex gap-2">
          {!batchMode ? (
            <button
              onClick={() => setBatchMode(true)}
              className="px-4 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              disabled={prompts.length === 0}
            >
              批量操作
            </button>
          ) : (
            <>
              <button
                onClick={handleBatchRestore}
                className="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                disabled={selectedPrompts.size === 0}
              >
                批量恢复 ({selectedPrompts.size})
              </button>
              <button
                onClick={handleBatchPermanentDelete}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                disabled={selectedPrompts.size === 0}
              >
                批量删除 ({selectedPrompts.size})
              </button>
              <button
                onClick={() => {
                  setBatchMode(false);
                  setSelectedPrompts(new Set());
                }}
                className="px-4 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                取消
              </button>
            </>
          )}
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* 提示词列表 */}
      {prompts.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🗑️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">回收站是空的</h3>
          <p className="text-gray-500">
            {searchQuery ? '没有找到匹配的提示词' : '暂时没有已删除的提示词'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {prompts.map((prompt) => {
            const remainingDays = getRemainingDays(prompt.deletedAt || Date.now());
            const isExpiringSoon = remainingDays <= 2;
            
            return (
              <div key={prompt.id} className="relative">
                {/* 过期警告标签 */}
                {isExpiringSoon && (
                  <div className="absolute -top-2 -right-2 z-10 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {remainingDays === 0 ? '今天过期' : `${remainingDays}天后过期`}
                  </div>
                )}
                
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <PromptCard
                    prompt={prompt}
                    onViewDetail={() => handleViewPrompt(prompt)}
                    onEdit={() => {}} // 回收站中不允许编辑
                    onDelete={() => {}} // 使用自定义删除逻辑
                    onClone={() => {}} // 回收站中不允许克隆
                    onToggleFavorite={() => {}} // 回收站中不允许收藏
                    onUse={() => {}} // 回收站中不允许使用
                    isBatchMode={batchMode}
                    isSelected={selectedPrompts.has(prompt.id)}
                    onSelect={batchMode ? (promptId: string, selected: boolean) => handlePromptSelect(promptId, selected) : undefined}
                  />
                  
                  {/* 回收站操作按钮 */}
                  <div className="px-4 pb-4 flex gap-2 bg-gray-50 border-t">
                    <button
                      onClick={() => handleRestore(prompt.id)}
                      className="flex-1 px-3 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                    >
                      恢复
                    </button>
                    <button
                      onClick={() => handlePermanentDelete(prompt.id)}
                      className="flex-1 px-3 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                    >
                      永久删除
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 提示词详情模态框 */}
      {showModal && selectedPrompt && (
        <PromptDetailModal
          prompt={selectedPrompt}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedPrompt(null);
          }}
          onEdit={() => {}} // 回收站中不允许编辑
          onUse={() => {}} // 回收站中不允许使用
          onToggleFavorite={() => {}} // 回收站中不允许收藏
        />
      )}
    </div>
  );
} 